import React from 'react'
import useLinkPreview from '../custom-hooks/useLinkPreview'
import ReactMarkdown from 'react-markdown'

const LinkPreviewWithText = ({ content, isLoggedUser }) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g
  const urls = content.match(urlRegex)
  const url = urls ? urls[0] : null // Take the first URL
  const { preview, loadingLink, error } = useLinkPreview(url)

  // Extract text before and after the URL
  const textParts = url ? content.split(url) : [content]
  const textBefore = textParts[0] || ''
  const textAfter = textParts[1] || ''

  // Extract domain from URL for fallback
  const getDomainFromUrl = url => {
    try {
      const urlObj = new URL(url)
      const hostname = urlObj.hostname.replace('www.', '') // e.g., "google.com"
      const domainWithoutExt = hostname.split('.')[0] // e.g., "google"
      return {
        domainWithoutExt, // For title (e.g., "google")
        fullDomain: hostname // For description (e.g., "google.com")
      }
    } catch (e) {
      return {
        domainWithoutExt: url,
        fullDomain: url
      } // Fallback to full URL if parsing fails
    }
  }
  const { domainWithoutExt, fullDomain } = url
    ? getDomainFromUrl(url)
    : { domainWithoutExt: '', fullDomain: '' }

  return (
    <div className="d-flex flex-column-reverse gap-1">
      {/* Display text before the URL */}
      {textBefore.trim() && (
        <span
          style={{
            color: isLoggedUser ? '#ffffff' : '#000',
            wordWrap: 'break-word',
            overflowWrap: 'break-word'
          }}
        >
          <ReactMarkdown
            components={{
              p: ({ node, ...props }) => (
                <p
                  style={{
                    color: isLoggedUser ? '#ffffff' : '#000',
                    margin: '0'
                  }}
                  {...props}
                />
              ),
              strong: ({ node, ...props }) => (
                <strong
                  style={{
                    color: isLoggedUser ? '#ffffff' : '#000',
                    fontWeight: 600
                  }}
                  {...props}
                />
              )
            }}
          >
            {textBefore}
          </ReactMarkdown>
        </span>
      )}

      {/* Display the URL and its preview */}
      {url && (
        <>
          <a
            className="px-1"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: isLoggedUser ? '#ffffff' : '#000000',
              textDecoration: 'underline',
              display: 'block',
              fontWeight: '500'
            }}
          >
            {url.length > 45 ? url.substring(0, 46) + '...' : url}
          </a>
          {loadingLink ? (
            <div>Loading preview...</div>
          ) : error || !preview ? (
            <div
              style={{
                marginTop: '5px',
                padding: '10px',
                backgroundColor: isLoggedUser
                  ? 'rgba(255, 255, 255, 0.1)'
                  : '#c3c3c394',
                borderRadius: '8px'
              }}
            >
              <a href={url} target="_blank" rel="noopener noreferrer">
                <div>
                  <strong
                    style={{
                      color: isLoggedUser ? '#ffffff' : '#000',
                      fontSize: '16.5px'
                    }}
                  >
                    {domainWithoutExt}
                  </strong>
                  <p
                    style={{
                      color: isLoggedUser ? '#ffffff' : '#555',
                      fontSize: '12px',
                      margin: '0'
                    }}
                  >
                    {fullDomain}
                  </p>
                </div>
              </a>
            </div>
          ) : (
            <div
              style={{
                marginTop: '5px',
                padding: '10px',
                backgroundColor: isLoggedUser
                  ? 'rgba(255, 255, 255, 0.1)'
                  : '#c3c3c394',
                borderRadius: '8px'
              }}
            >
              <a href={url} target="_blank" rel="noopener noreferrer">
                {preview.images &&
                  preview.images.length > 0 &&
                  preview.images.map((image, index) => (
                    <img
                      key={index}
                      src={image}
                      alt={`Preview ${index + 1}`}
                      style={{
                        width: '100%',
                        borderRadius: '5px',
                        marginBottom: '5px'
                      }}
                      onError={e => {
                        e.target.onerror = null // Prevent infinite loop
                        e.target.src =
                          'https://icon-library.com/images/anonymous-avatar-icon/anonymous-avatar-icon-25.jpg' // Fallback image
                      }}
                    />
                  ))}
                <div>
                  <strong
                    style={{
                      color: isLoggedUser ? '#ffffff' : '#000',
                      fontSize: '16.5px'
                    }}
                  >
                    {preview.title}
                  </strong>
                  <p
                    style={{
                      color: isLoggedUser ? '#ffffff' : '#555',
                      fontSize: '12px',
                      margin: '0'
                    }}
                  >
                    {preview.description.length > 35
                      ? preview.description.substring(0, 36) + '...'
                      : preview.description}
                  </p>
                  <p
                    style={{
                      color: isLoggedUser ? '#ffffff' : '#555',
                      fontSize: '10px',
                      margin: '0'
                    }}
                  >
                    {fullDomain}
                  </p>
                </div>
              </a>
            </div>
          )}
        </>
      )}

      {/* Display text after the URL */}
      {textAfter.trim() && (
        <span
          style={{
            color: isLoggedUser ? '#ffffff' : '#000',
            wordWrap: 'break-word',
            overflowWrap: 'break-word'
          }}
        >
          <ReactMarkdown
            components={{
              p: ({ node, ...props }) => (
                <p
                  style={{
                    color: isLoggedUser ? '#ffffff' : '#000',
                    margin: '0'
                  }}
                  {...props}
                />
              ),
              strong: ({ node, ...props }) => (
                <strong
                  style={{
                    color: isLoggedUser ? '#ffffff' : '#000',
                    fontWeight: 600
                  }}
                  {...props}
                />
              )
            }}
          >
            {textAfter}
          </ReactMarkdown>
        </span>
      )}
    </div>
  )
}

export default LinkPreviewWithText
